import { useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Chevron, Confidence, Segmented, TypeBadge, TypeChip, typeOf, type TypeKey } from '../Page'
import { Glyph } from './Glyph'
import { PageWell, WellButton, WellChip, type WellHandle } from './PageWell'
import { PassageText } from './Text'
import {
  TYPE_ORDER,
  columnsLabel,
  passagesOf,
  regionName,
  toPt,
  type PageGeometry,
  type RegionItem,
} from './model'
import type { RegionRecord } from './useRegionDetail'
import type { PageDetail, PageHit, Region } from '../../lib/api'

/**
 * The phone.
 *
 * A broadsheet is 792 points wide and a phone is 390. The page cannot be made
 * to fit, so it is not made to fit: the page is the whole screen and
 * everything else is one sheet that slides over it at three heights. Chrome
 * overlays or leaves, and never takes room from the page.
 */

export type SheetHeight = 'handle' | 'half' | 'full'

const HEIGHTS: Record<SheetHeight, string> = {
  handle: '66px',
  half: '47%',
  full: '88%',
}

export function MobileViewer({
  page,
  geometry,
  regions,
  visibleTypes,
  onToggleType,
  counts,
  overlayOn,
  onOverlay,
  selected,
  record,
  onSelect,
  hits,
  activeHit,
  onActivateHit,
  query,
  corrected,
  onCorrected,
  onWell,
  onTransform,
  zoomPercent,
  selecting,
  onMarquee,
  pendingBox,
  onClip,
  canClip,
  sheet,
  onSheet,
  chromeHidden,
  onChrome,
  selectionBox,
  clipPanel,
  pdfHref = null,
}: {
  page: PageDetail
  geometry: PageGeometry
  regions: RegionItem[]
  visibleTypes: Set<TypeKey>
  onToggleType: (type: TypeKey) => void
  counts: Record<TypeKey, number>
  overlayOn: boolean
  onOverlay: (on: boolean) => void
  selected: RegionItem | null
  record: RegionRecord | null
  onSelect: (region: RegionItem | null) => void
  hits: PageHit[]
  activeHit: number
  onActivateHit: (index: number) => void
  query: string
  corrected: boolean
  onCorrected: (next: boolean) => void
  onWell: (handle: WellHandle) => void
  onTransform: (percent: number) => void
  zoomPercent: number
  selecting: boolean
  onMarquee: (bbox: Region) => void
  pendingBox: Region | null
  onClip: () => void
  canClip: boolean
  sheet: SheetHeight
  onSheet: (height: SheetHeight) => void
  chromeHidden: boolean
  onChrome: (hidden: boolean) => void
  selectionBox: Region | null
  clipPanel: ReactNode | null
  /** The whole issue as its original PDF, opened at this page. */
  pdfHref?: string | null
}) {
  const dragStart = useRef<{ y: number; from: SheetHeight } | null>(null)
  const [dragging, setDragging] = useState(false)

  const well = (
    <PageWell
      pageId={page.id}
      geometry={geometry}
      regions={regions}
      visibleTypes={visibleTypes}
      overlayOn={overlayOn && !chromeHidden}
      selectedKey={selected?.key ?? null}
      selectionBox={selectionBox}
      onSelect={(region) => {
        onSelect(region)
        if (region) onSheet('half')
      }}
      hits={hits}
      activeHit={activeHit}
      onActivateHit={onActivateHit}
      selecting={selecting}
      onMarquee={onMarquee}
      pendingBox={pendingBox}
      onTransform={onTransform}
      onReady={onWell}
      labelsOnSelectionOnly
      touch
      showNavigator={false}
      showControls={false}
    >
      {chromeHidden ? (
        <button
          type="button"
          className="absolute inset-0 z-10"
          aria-label="Bring the controls back"
          onClick={() => onChrome(false)}
        >
          <span className="absolute bottom-4 left-1/2 -translate-x-1/2">
            <WellChip>
              <span className="font-mono text-[9.5px] tracking-[0.1em]">
                TAP TO BRING BACK THE CONTROLS
              </span>
            </WellChip>
          </span>
        </button>
      ) : (
        <>
          <div className="absolute left-3 top-3 z-10 flex gap-1.5">
            <WellButton label="Reading mode: hide the controls" size={40} onClick={() => onChrome(true)}>
              <Glyph name="fit" size={16} />
            </WellButton>
          </div>
          <div
            className="absolute right-3 top-3 z-10 rounded-[--radius-md] px-2 py-1.5 font-mono text-[10px] leading-[1.5]"
            style={{
              background: 'color-mix(in srgb, var(--jl-well) 86%, transparent)',
              border: '1px solid color-mix(in srgb, white 22%, var(--jl-well))',
              color: 'color-mix(in srgb, white 88%, var(--jl-well))',
            }}
          >
            <div>{zoomPercent}%</div>
            {query && (
              <div style={{ color: 'color-mix(in srgb, white 58%, var(--jl-well))' }}>
                {hits.length ? `HIT ${activeHit + 1}/${hits.length}` : 'NO HITS HERE'}
              </div>
            )}
          </div>
        </>
      )}
    </PageWell>
  )

  if (chromeHidden) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-well">
        {well}
        <div className="flex h-[22px] flex-none items-center justify-center bg-well">
          <span
            className="h-1 w-[116px] rounded-full"
            style={{ background: 'color-mix(in srgb, white 30%, var(--jl-well))' }}
          />
        </div>
      </div>
    )
  }

  const startDrag = (event: React.PointerEvent) => {
    dragStart.current = { y: event.clientY, from: sheet }
    setDragging(true)
    ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
  }
  const endDrag = (event: React.PointerEvent) => {
    const start = dragStart.current
    dragStart.current = null
    setDragging(false)
    if (!start) return
    const delta = start.y - event.clientY
    const order: SheetHeight[] = ['handle', 'half', 'full']
    let index = order.indexOf(start.from)
    if (delta > 40) index = Math.min(order.length - 1, index + 1)
    else if (delta < -40) index = Math.max(0, index - 1)
    else index = order.indexOf(start.from) === 2 ? 1 : Math.min(2, index + 1)
    onSheet(order[index])
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-12 flex-none items-center gap-2.5 border-b border-n300 px-3">
        <Link
          to="/search"
          aria-label="Back to the search"
          className="-ml-2 flex h-10 w-10 items-center justify-center text-ink no-underline"
        >
          <Chevron direction="left" size={16} />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[12px] leading-tight">
            {geometry.pubDate} · {geometry.label}
          </div>
          <div className="truncate text-[11.5px] text-ink2">
            {geometry.publication ?? 'This publication'}
            {page.source_type !== 'digital' && geometry.pageCount
              ? ` · Scanned edition · ${geometry.pageCount} pages`
              : ''}
          </div>
        </div>
        {pdfHref && (
          <a
            href={pdfHref}
            target="_blank"
            rel="noopener"
            className="flex h-10 items-center px-1 text-[11px] uppercase tracking-[0.11em] text-accent no-underline"
          >
            PDF
          </a>
        )}
        <Link
          to={`/search?q=${encodeURIComponent(query)}`}
          aria-label="Search the archive"
          className="flex h-10 w-10 items-center justify-center text-ink no-underline"
        >
          <Glyph name="search" size={15} />
        </Link>
      </div>

      {well}

      {sheet === 'handle' && (
        <div className="flex flex-none gap-1.5 overflow-x-auto border-t border-n300 px-3 py-2">
          <Segmented
            ariaLabel="Overlay"
            accent
            value={overlayOn ? 'annotated' : 'clean'}
            onChange={(next) => onOverlay(next === 'annotated')}
            options={[
              { value: 'clean', label: 'Clean' },
              { value: 'annotated', label: 'Annotated' },
            ]}
          />
          {TYPE_ORDER.slice(0, 3).map((type) => (
            <TypeChip
              key={type}
              type={type}
              count={counts[type]}
              active={visibleTypes.has(type)}
              onClick={() => onToggleType(type)}
            />
          ))}
        </div>
      )}

      <div
        className="flex flex-none flex-col overflow-hidden border-t border-rule2 bg-paper"
        style={{
          height: HEIGHTS[sheet],
          transition: dragging ? 'none' : 'height .32s var(--jl-ease)',
          boxShadow: sheet === 'handle' ? 'none' : '0 -12px 32px rgba(45,43,43,0.22)',
          background: sheet === 'handle' ? 'var(--color-surface)' : 'var(--color-bg)',
        }}
      >
        <button
          type="button"
          aria-label={`Sheet at ${sheet} height. Drag or tap to change.`}
          onPointerDown={startDrag}
          onPointerUp={endDrag}
          className="flex h-6 flex-none items-center justify-center pt-2"
        >
          <span className="h-1 w-[34px] rounded-full bg-rule2" />
        </button>

        {sheet === 'handle' || !selected || !record ? (
          <div className="flex flex-1 items-center gap-2.5 px-3 pb-2">
            <div className="flex-1 text-[13.5px] text-ink2">
              {selected && record
                ? `${typeOf(selected.rawType).name}: ${regionName(selected.rawType, record.title)}`
                : 'Tap a region, or drag up for the page contents'}
            </div>
            <div className="whitespace-nowrap font-mono text-[10px] text-ink2">
              {regions.length} REGIONS
            </div>
          </div>
        ) : (
          <MobileRecord
            geometry={geometry}
            region={selected}
            record={record}
            query={query}
            corrected={corrected}
            onCorrected={onCorrected}
            onClip={onClip}
            canClip={canClip}
            full={sheet === 'full'}
          />
        )}
      </div>

      {clipPanel && (
        <div className="absolute inset-x-0 bottom-0 z-30 max-h-[80%] overflow-y-auto border-t border-rule2 bg-paper">
          {clipPanel}
        </div>
      )}

      <div className="flex h-[22px] flex-none items-center justify-center">
        <span className="h-1 w-[116px] rounded-full bg-rule2" />
      </div>
    </div>
  )
}

function MobileRecord({
  geometry,
  region,
  record,
  query,
  corrected,
  onCorrected,
  onClip,
  canClip,
  full,
}: {
  geometry: PageGeometry
  region: RegionItem
  record: RegionRecord
  query: string
  corrected: boolean
  onCorrected: (next: boolean) => void
  onClip: () => void
  canClip: boolean
  full: boolean
}) {
  const passages = passagesOf(region, corrected)
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex-none border-b border-n300 px-3.5 pb-3 pt-2.5">
        <div className="mb-1.5 flex items-center gap-2">
          <TypeBadge type={region.rawType} />
          <div className="font-mono text-[10px] text-ink2">
            ORD {String(region.ordinal).padStart(2, '0')}
            {record.confidence !== null && record.confidence !== undefined
              ? ` · ${(record.confidence * 100).toFixed(1)}%`
              : ''}
          </div>
          {record.needsReview && (
            <div className="ml-auto font-mono text-[10px] text-mark">REVIEW QUEUED</div>
          )}
        </div>
        <div
          className="mb-1 overflow-hidden text-[20px] font-semibold leading-[1.18]"
          style={{
            letterSpacing: '-0.01em',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {regionName(region.rawType, record.title)}
        </div>
        <div className="text-[13.5px] text-ink2">
          {/* A photograph's credit is not a byline, so it is not prefixed. */}
          {record.byline
            ? `${region.typeKey === 'article' || region.typeKey === 'notice' ? 'By ' : ''}${record.byline.replace(/^by\s+/i, '')}`
            : 'Unattributed'}
        </div>
      </div>

      <div className="flex-none border-b border-n300 bg-surface px-3.5 py-2 font-mono text-[11px] leading-[1.65]">
        <div>
          {`${Math.round(region.bbox[0])}, ${Math.round(region.bbox[1])} · ${Math.round(
            region.bbox[2] - region.bbox[0],
          )} × ${Math.round(region.bbox[3] - region.bbox[1])} px`}
        </div>
        <div className="text-ink2">
          {`${toPt(region.bbox[0], geometry.ppi).toFixed(1)}, ${toPt(
            region.bbox[1],
            geometry.ppi,
          ).toFixed(1)} pt · cols ${columnsLabel(region.bbox, geometry)}`}
        </div>
      </div>

      {full && (
        <div className="flex flex-none items-center gap-2.5 border-b border-n300 px-3.5 py-2">
          <Segmented
            ariaLabel="Which text to show"
            size="sm"
            value={corrected ? 'corrected' : 'recognised'}
            onChange={(next) => onCorrected(next === 'corrected')}
            options={[
              { value: 'recognised', label: 'Recognised' },
              {
                value: 'corrected',
                label: 'Corrected',
                disabled: !region.blocks.some((block) => block.corrected_text),
              },
            ]}
          />
          <div className="ml-auto">
            <Confidence value={record.confidence} width={40} />
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-3.5 py-2.5">
        {passages.length ? (
          <PassageText passages={full ? passages : passages.slice(0, 1)} query={query} size={15.5} />
        ) : (
          <p className="text-[14.5px] text-ink2">No text was extracted for this region.</p>
        )}
      </div>

      <div className="flex flex-none gap-1.5 border-t border-n300 px-3.5 py-2.5">
        <button
          type="button"
          className="btn-primary h-[46px] flex-1 text-[15px]"
          onClick={onClip}
          disabled={!canClip}
        >
          Clip
        </button>
        <Link
          to={record.unitId ? `/unit/${record.unitId}` : `/article/${record.articleId ?? ''}`}
          className="btn-secondary h-[46px] flex-1 text-[15px] no-underline"
        >
          Cite
        </Link>
        <button
          type="button"
          aria-label="Copy the permalink"
          className="btn-secondary h-[46px] w-[46px] px-0"
          onClick={() =>
            void navigator.clipboard?.writeText(
              `${window.location.origin}/p/${geometry.slug}/${geometry.pubDate}/${geometry.label}`,
            )
          }
        >
          <Glyph name="jump" size={15} />
        </button>
      </div>
    </div>
  )
}
